import { useEffect, useRef } from 'react';
import { App, Button, Divider, Drawer, Form, Space } from 'antd';
import type { ContactListItem, FieldDefinitionDto } from '@crm/shared';
import { RecordForm } from '../../components/RecordForm';
import { fromFormValues, toFormValues } from '../../components/DynamicField';
import { useCreateContact, useUpdateContact } from '../../features/contacts/api';
import { errorMessage } from '../../api/client';
import { ChannelsInput, emailRows, emailRowsRule, phoneRows, toEmailsPayload, toPhonesPayload } from '../../components/ContactChannels';
import { AddressesInput, toAddressesPayload } from '../../components/Addresses';
import { OrganizationSelect } from '../../components/OrganizationSelect';

/**
 * Shown as list editors (Emails, Phones, Organizations) instead of single inputs; the API fills these columns
 * from the lists.
 */
const LIST_KEYS = ['email', 'phone', 'mobile', 'organization_id'];

interface Props {
  open: boolean;
  onClose: () => void;
  defs: FieldDefinitionDto[];
  contact?: ContactListItem | null;
  /** Preset values for a new contact (e.g. organizationId from the org page). */
  preset?: Record<string, unknown>;
  onSaved?: (c: ContactListItem) => void;
}

export function ContactDrawer({ open, onClose, defs, contact, preset, onSaved }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const create = useCreateContact();
  const update = useUpdateContact();

  // Only (re)initialise when the drawer opens or the edited record changes; never on background
  // re-renders, so user input is not wiped by a prop identity change.
  const latest = useRef({ defs, preset });
  latest.current = { defs, preset };
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      contact
        ? { ...toFormValues(latest.current.defs, contact as unknown as Record<string, unknown>), emails: emailRows(contact.emails), phones: phoneRows(contact.phones), organizationIds: contact.organizations.map((o) => o.id), addresses: contact.addresses ?? [] }
        : { fields: {}, emails: [], phones: [], addresses: [], subscribed: true, ...presetValues(latest.current.preset) },
    );
  }, [open, contact?.id, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (values: Record<string, unknown>) => {
    const { email: _e, phone: _p, mobile: _m, organizationId: _o, ...payload } = fromFormValues(defs, values);
    payload.organizationIds = (values.organizationIds as string[] | undefined) ?? [];
    payload.emails = toEmailsPayload(values.emails as never);
    payload.phones = toPhonesPayload(values.phones as never);
    payload.addresses = toAddressesPayload(values.addresses as never);
    try {
      const saved = contact
        ? await update.mutateAsync({ id: contact.id, ...payload })
        : await create.mutateAsync(payload as Parameters<typeof create.mutateAsync>[0]);
      message.success(contact ? 'Contact updated' : 'Contact created');
      onSaved?.(saved);
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Drawer
      title={contact ? 'Edit contact' : 'New contact'}
      open={open}
      onClose={onClose}
      size={640}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={create.isPending || update.isPending}>
            Save
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
        <RecordForm
          defs={defs}
          form={form}
          hidden={LIST_KEYS}
          groupExtras={groupExtras(defs)}
        />
        <Divider titlePlacement="start">Addresses</Divider>
        <Form.Item name="addresses">
          <AddressesInput />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

/** A preset `organizationId` (the organization page's "Add contact") starts the organizations list. */
function presetValues(preset: Record<string, unknown> | undefined) {
  const { organizationId, ...rest } = preset ?? {};
  return { ...rest, organizationIds: organizationId ? [organizationId] : [] };
}

/** The list editors, each in its field's group (both in one when they share it). */
function groupExtras(defs: FieldDefinitionDto[]) {
  const channelsGroup = defs.find((d) => d.key === 'email')?.group ?? 'Basic';
  const org = defs.find((d) => d.key === 'organization_id');
  const organizations = org && (
    <Form.Item name="organizationIds" label="Organizations" tooltip="The first one is the primary organization">
      <OrganizationSelect multiple />
    </Form.Item>
  );
  const channels = (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', columnGap: 16 }}>
      <Form.Item name="emails" label="Emails" rules={[emailRowsRule]}>
        <ChannelsInput kind="email" />
      </Form.Item>
      <Form.Item name="phones" label="Phones">
        <ChannelsInput kind="phone" />
      </Form.Item>
    </div>
  );
  if (!org || org.group === channelsGroup) return { [channelsGroup]: <>{channels}{organizations}</> };
  return { [channelsGroup]: channels, [org.group ?? 'Other']: organizations };
}
